import { DemoIndicatorColoredIcon, PlusOutlineIcon } from '@carboncopyco/icons';
import {
  DemosIllustration,
  SearchNoMatchIllustration,
} from '@carboncopyco/illustrations';
import {
  type CreatorFilter,
  type IDemo,
  PermissionAction,
  PermissionKey,
  RecordingTypes,
  type Sort,
  type Tour,
} from '@carboncopyco/types';
import {
  Badge,
  Box,
  Button,
  EmptyView,
  Flex,
  Grid,
  InputText,
  LoadingDots,
  Modal,
  SkeletonImageLoading,
  Spacer,
  TextBase,
  TextLargeParagraph,
  TooltipBubble,
} from '@carboncopyco/ui';
import {
  AnalyticsTrackEventName,
  MMMDoYYYY,
  useAnalytics,
} from '@carboncopyco/utils';
import { useBoolean } from 'ahooks';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { useDebounce } from 'use-debounce';

import {
  selectOrganizationId,
  useGetDemosQuery,
  useInfiniteScroll,
  useLazyGetSearchResultsQuery,
} from '@carboncopyco/data';
import { useWorkspacesMap } from '../../../hooks/useWorkspacesMap';
import { CreateDemoModal, CreateEntityModal } from '../../Demo';
import { TileImageSquare } from '../../GridTiles';
import { InfiniteList } from '../../InfiniteList';
import SearchControls from '../../SearchControls';
import { creatorFilterOptions, sortOptions } from '../../consts';
import { usePermissionsContext } from '../../utils';

interface CreateTourModalProps {
  open: boolean;
  onClose: () => void;
  title?: string;
  description?: string;
  originalTourId?: string;
  selectedFrameIndex?: number;
  demoIdsTourIsBasedOn?: number[];
  createTourFromDemo: (
    id: number,
    originalTourId?: Tour['id'],
    insertIndex?: number,
    folderId?: string
  ) => void;
  goToDemo: (demoId: string, workspaceId: string) => void;
  handleOpenRecorder: () => void;
  goToCreateOverlayDemoPage: () => void;
  closeAllModals?: () => void;
  folderId?: string;
}

export const CreateTourModal = ({
  open,
  onClose,
  title = 'Create a new tour',
  description = 'Choose a demo to capture your tour flow.',
  originalTourId = '',
  selectedFrameIndex,
  demoIdsTourIsBasedOn,
  createTourFromDemo,
  handleOpenRecorder,
  goToCreateOverlayDemoPage,
  goToDemo,
  closeAllModals,
  folderId,
}: CreateTourModalProps) => {
  const {
    items,
    isLoading,
    isLoadingBatch,
    fetchFirstBatch,
    fetchNextBatch,
    error,
  } = useInfiniteScroll(useLazyGetSearchResultsQuery);
  const organizationId = useSelector(selectOrganizationId);

  const { workspaceId } = useParams<{
    workspaceId: string;
  }>();
  const { getPermissions } = usePermissionsContext();
  const createADemoPlanPermission = getPermissions(
    PermissionKey.demo,
    PermissionAction.createADemo
  );

  const [showCreateDemoModal, setShowCreateDemoModal] = useBoolean();
  const [
    showSelectBaseDemoModal,
    {
      setTrue: setTrueShowSelectBaseDemoModal,
      setFalse: setFalseShowSelectBaseDemoModal,
    },
  ] = useBoolean();
  const [searchValue, setSearchValue] = useState('');
  const [query] = useDebounce(searchValue, 500);
  const [creatorFilter, setCreatorFilter] = useState<CreatorFilter>('all');
  const [sort, setSort] = useState<Sort>('alphabet');
  const { trackEvent } = useAnalytics();
  const resetFilters = () => {
    setCreatorFilter('all');
    setSort('alphabet');
  };
  const flags = useFlags();

  const recordingTypesFilter = [
    RecordingTypes.dynamic,
    RecordingTypes.overlay,
    RecordingTypes.native,
  ];
  const { data: demoResponse, isLoading: isLoadingDemos } = useGetDemosQuery({
    limit: 100,
    organizationId,
    workspaces: workspaceId,
    recordingType: recordingTypesFilter,
  });
  const demos = demoResponse?.items;
  const workspacesMap = useWorkspacesMap();
  const isAllControlsDefault = creatorFilter === 'all' && sort === 'alphabet';

  useEffect(() => {
    fetchFirstBatch({
      includeShared: false,
      organizationId,
      workspaces: workspaceId ?? 'all',
      query,
      mineOnly: creatorFilter === 'mineOnly',
      entities: ['demo'],
      sort,
      recordingType: recordingTypesFilter,
    });
  }, [creatorFilter, sort, query, workspaceId]);

  const handleClose = () => {
    setSearchValue('');
    onClose();
  };

  const modalTitle = demos?.length ? (
    <div className="flex items-center">{title}</div>
  ) : (
    ''
  );

  const getIsTourBasedOnDemo = (demoId: number) => {
    return demoIdsTourIsBasedOn?.find(
      (demoIdTourIsBasedOn) => demoIdTourIsBasedOn === demoId
    );
  };

  const handleOnClose = () => {
    handleClose();
    setFalseShowSelectBaseDemoModal();
    setShowCreateDemoModal.setFalse();
  };

  const handleContinue = (selected) => {
    if (selected === 'clone') {
      setShowCreateDemoModal.setTrue();
      trackEvent(AnalyticsTrackEventName.TOUR_CLICK_CREATE_FROM_DEMO, {
        location: 'create_demo_modal',
        organizationId,
        workspaceId,
      });
      return;
    }
    trackEvent(AnalyticsTrackEventName.TOUR_CLICK_CREATE_FROM_WEB, {
      location: 'create_demo_modal',
      organizationId,
      workspaceId,
    });
    handleOnClose();
    goToCreateOverlayDemoPage();
  };

  const handleOnItemClick = (item) => {
    trackEvent(AnalyticsTrackEventName.TOUR_CLICK_CHOOSE_BASE_DEMO, {
      location: 'create_tour_modal',
      demoId: item.id,
      workspaceId,
      organizationId,
    });
    if (originalTourId) {
      trackEvent(AnalyticsTrackEventName.TOUR_SELECT_ASSET, {
        location: 'tour_add_frame_modal',
        demoId: item.id,
        workspaceId,
        organizationId,
      });
    }
    closeAllModals?.();
    createTourFromDemo(
      item.id,
      Number(originalTourId),
      selectedFrameIndex ? selectedFrameIndex + 1 : 1,
      folderId
    );
  };

  const renderItem = (itemData, selected, onItemClick) => {
    const demo = itemData as IDemo;
    const demoName = demo.demoName || 'Untitled demo';
    const workspace = workspacesMap[itemData.workspaceId] ?? {
      workspaceName: 'Untitled workspace',
    };
    return (
      <TooltipBubble
        title={`${demoName}, ${workspace.workspaceName}`}
        placement="top"
        interactive={false}
        followCursor
        disabled={!flags.platformWorkspaces}
      >
        <TileImageSquare
          selected={itemData.id === selected}
          onClick={() => onItemClick(itemData)}
          title={demoName}
          caption={flags.platformWorkspaces ? workspace.workspaceName : ''}
          createTime={MMMDoYYYY(itemData.createTime)}
          image={itemData.image}
          icon={<DemoIndicatorColoredIcon size={30} />}
          imageLabel={
            getIsTourBasedOnDemo(Number.parseInt(String(itemData.id), 10)) && (
              <Badge
                label="Tour based on"
                variant="yellow"
              />
            )
          }
        />
      </TooltipBubble>
    );
  };

  const handleNewDemo = () => {
    createADemoPlanPermission
      ? setTrueShowSelectBaseDemoModal()
      : goToCreateOverlayDemoPage();
    handleClose();
  };

  return (
    <>
      {showCreateDemoModal && (
        <CreateDemoModal
          open={showCreateDemoModal}
          onClose={handleOnClose}
          handleOpenRecorder={handleOpenRecorder}
          goToDemo={goToDemo}
        />
      )}
      {showSelectBaseDemoModal && (
        <CreateEntityModal
          base="demo"
          open={showSelectBaseDemoModal}
          onClose={() => {
            handleClose();
            setFalseShowSelectBaseDemoModal();
          }}
          handleContinue={handleContinue}
          isTourFromUrlDisabled={!demos?.length}
        />
      )}
      <Modal
        onClose={handleClose}
        open={open}
        title={modalTitle}
      >
        <div className="w-ds-lg-modal h-ds-440 flex flex-col">
          {isLoadingDemos ? (
            <div className="w-full h-full flex justify-center items-center">
              <LoadingDots />
            </div>
          ) : demos?.length ? (
            <>
              <Spacer amount={1.75} />
              <TextBase
                label={description}
                color="text-ds-natural-300"
              />
              <Spacer amount={0.75} />

              <div className="flex justify-between">
                <SearchControls
                  creatorFilterDropdown={{
                    value: creatorFilter,
                    onSelect: setCreatorFilter,
                    items: creatorFilterOptions,
                  }}
                  sortDropdown={{
                    value: sort,
                    onSelect: setSort,
                    items: sortOptions,
                  }}
                />
                <div className="w-96">
                  <InputText
                    value={searchValue}
                    placeholder="Search"
                    onChange={(event) => {
                      setSearchValue(event.currentTarget.value);
                    }}
                  />
                </div>
              </div>
              <Spacer amount={1.75} />
              <Box className="flex-grow border border-ds-natural-100 rounded-md">
                {isLoading || items?.length || !query ? (
                  <InfiniteList
                    error={
                      error && {
                        message: 'Error fetching demos.',
                      }
                    }
                    isLoading={isLoading}
                    isLoadingBatch={isLoadingBatch}
                    loader={<SkeletonImageLoading cols={4} />}
                    onReachBottom={() => {
                      fetchNextBatch({
                        includeShared: false,
                        organizationId,
                        workspaces: workspaceId ?? 'all',
                        query,
                        mineOnly: creatorFilter === 'mineOnly',
                        entities: ['demo'],
                        sort,
                        recordingType: recordingTypesFilter,
                      });
                    }}
                  >
                    <Grid
                      data={items}
                      onItemClick={handleOnItemClick}
                      renderItem={renderItem}
                    />
                  </InfiniteList>
                ) : (
                  <EmptyView
                    illustration={<SearchNoMatchIllustration />}
                    title={
                      <Flex
                        col
                        alignItems="center"
                      >
                        <div className="mb-ds-4">
                          <TextLargeParagraph
                            color="text-ds-natural-600"
                            label={`No matches found for "${query}"`}
                          />
                        </div>
                        <Button
                          secondary
                          label="Clear all filters"
                          onClick={() => {
                            // eslint-disable-next-line @typescript-eslint/no-unused-expressions
                            resetFilters?.();
                          }}
                          disabled={isAllControlsDefault}
                        />
                      </Flex>
                    }
                  />
                )}
              </Box>
            </>
          ) : (
            <div className="flex flex-col justify-center items-center pt-24">
              <EmptyView
                noPadding
                title="But first, create a demo"
                illustration={<DemosIllustration />}
                message="In order to make a tour you need to create a demo."
              />
              <Spacer amount={2} />
              <Button
                label="New demo"
                leftIcon={<PlusOutlineIcon />}
                onClick={handleNewDemo}
              />
            </div>
          )}
        </div>
      </Modal>
    </>
  );
};
